import moment from 'moment';
import * as React from 'react';
import { APIError } from '../../APIError';
import { mergeClasses } from '../../constants';
import Panel from '../../widgets/Panel';
import Sparkline from '../../widgets/Sparkline';
import StatusRenderer from '../../widgets/StatusRenderer';
import { SensorWithMetrics } from './SensorDetailsView';

type SensorMetricListProps = {
  sensors: Array<SensorWithMetrics>;
  showStatus: boolean;
  title: string;
  error: APIError | null;
};

type SensorDetailRowProps = {
  className?: string;
  name: string;
  status: React.ReactNode;
  activity: React.ReactNode;
};

export class SensorDetailRow extends React.PureComponent<SensorDetailRowProps> {
  render() {
    const { className, name, status, activity } = this.props;
    return (
      <div className={mergeClasses(['d-flex', 'flex-table-row', className])}>
        <div className={`${status ? 'col-3' : 'col-6'} p-3`}>{name}</div>
        {status && <div className="col-3 p-3">{status}</div>}
        <div className="col-6 px-3 py-1" style={{ width: 0 }}>
          {activity}
        </div>
      </div>
    );
  }
}

export default class SensorMetricList extends React.Component<SensorMetricListProps> {
  static getSensorStatus(sensor: SensorWithMetrics): {
    status: string;
    label: string;
  } {
    const { state, health, type, shipped_date } = sensor;
    switch ((state || '').toUpperCase()) {
      case 'NEW':
      case 'PROVISIONED':
        if (type === 'vlc') {
          return { status: 'STAGED', label: 'Awaiting Activation' };
        } else {
          return { status: 'PACKED', label: 'Preparing to Ship' };
        }
      case 'SHIPPED':
        return {
          status: 'SHIPPED',
          label:
            'Shipped' +
            (shipped_date
              ? moment.utc(shipped_date).format('[ - ]MMM DD h:mma')
              : ''),
        };
      case 'STAGED':
        return { status: 'STAGED', label: 'Staged' };
      case 'INSTALLED':
        if (health === 'OK') {
          return { status: 'HEALTHY', label: 'Healthy' };
        } else {
          return { status: 'UNHEALTHY', label: 'Unhealthy' };
        }

      default:
        return { status: 'UNKNOWN', label: 'Unknown' };
    }
  }

  render() {
    const { sensors, showStatus, title, error } = this.props;
    // Using flexbox instead of a table for this view because the sparkline
    // chart does not compute width/handle resizing properly within a table cell
    return (
      <Panel title={title} className="mb-prs">
        <SensorDetailRow
          name="Name"
          className="header mt-4"
          status={showStatus ? 'Status' : null}
          activity="Activity (Past 24 Hours)"
        />
        {sensors.map((sensor) => (
          <SensorDetailRow
            key={sensor.id}
            name={sensor.shorthand || sensor.id}
            status={
              showStatus ? (
                <StatusRenderer {...SensorMetricList.getSensorStatus(sensor)} />
              ) : null
            }
            activity={
              error ? (
                <div className="sparkline">
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: '48px' }}
                  >
                    {error.message}
                  </div>
                </div>
              ) : (
                <Sparkline
                  data={sensor.obs || []}
                  tooltipValueSuffix={' obs'}
                />
              )
            }
          />
        ))}
      </Panel>
    );
  }
}
